import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as agentApi from '@/lib/agents/api';
import type { AgentConfig, CreateAgentInput } from '@/lib/agents/types';
import { AlertCircle, CheckCircle2, Loader2, Copy, Download, Upload, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import type { OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

// Lazy import Monaco for code splitting
const Editor = lazy(() => import('@monaco-editor/react').then((mod) => ({ default: mod.default })));

function EditorSkeleton() {
  return (
    <div className="w-full h-[500px] bg-muted/50 rounded-md border animate-pulse flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

type Monaco = Parameters<OnMount>[1];

// JSON Schema for agent configuration
const AGENT_JSON_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  required: ['name', 'agentType', 'config'],
  properties: {
    name: { type: 'string', description: 'Agent display name' },
    description: { type: 'string', description: 'Optional description' },
    agentType: {
      type: 'string',
      enum: ['chat', 'research', 'task', 'data-analysis', 'code-assistant', 'support', 'web-browsing', 'multi-agent', 'custom'],
      description: 'Type of agent',
    },
    config: {
      type: 'object',
      required: ['llmProvider'],
      properties: {
        llmProvider: {
          type: 'string',
          enum: ['anthropic', 'openai', 'google'],
          description: 'LLM provider to use',
        },
        model: { type: 'string', description: 'Specific model to use' },
        systemPrompt: { type: 'string', description: 'System instructions' },
        temperature: { type: 'number', minimum: 0, maximum: 2, description: 'Controls randomness (0-2)' },
        maxIterations: { type: 'integer', minimum: 1, description: 'Max tool call loops' },
        tools: { type: 'array', items: { type: 'string' }, description: 'Array of tool names to enable' },
      },
    },
    isPublic: { type: 'boolean', description: 'Whether agent is visible to organization' },
  },
};

// Default agent config template
const DEFAULT_CONFIG: AgentConfig = {
  llmProvider: 'anthropic',
  model: 'claude-sonnet-4-20250514',
  systemPrompt: 'You are a helpful AI assistant.',
  temperature: 0.7,
  maxIterations: 10,
};

const FULL_CONFIG_TEMPLATE = {
  name: 'My Agent',
  description: 'An AI agent built with the JSON builder',
  agentType: 'chat',
  config: DEFAULT_CONFIG,
  isPublic: false,
};

export function JsonBuilder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [jsonContent, setJsonContent] = useState(JSON.stringify(FULL_CONFIG_TEMPLATE, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [validating, setValidating] = useState(false);

  // Configure Monaco on mount
  const handleEditorMount: OnMount = (editorInstance: editor.IStandaloneCodeEditor, monaco: Monaco) => {

    // Configure JSON schema validation
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: 'https://matricks.ai/schemas/agent-config.json',
          fileMatch: ['*'],
          schema: AGENT_JSON_SCHEMA,
        },
      ],
      enableSchemaRequest: false,
      allowComments: false,
      trailingCommas: 'error',
    });

    // Set editor options
    editorInstance.updateOptions({
      minimap: { enabled: false },
      fontSize: 13,
      lineNumbers: 'on',
      wordWrap: 'on',
      formatOnPaste: true,
      automaticLayout: true,
      scrollBeyondLastLine: false,
      tabSize: 2,
    });
  };

  // Validate JSON on change
  useEffect(() => {
    try {
      const parsed = JSON.parse(jsonContent);

      // Basic validation
      const errors: string[] = [];
      if (!parsed.name || typeof parsed.name !== 'string') {
        errors.push('name is required and must be a string');
      }
      if (!parsed.agentType) {
        errors.push('agentType is required');
      }
      if (!parsed.config) {
        errors.push('config is required');
      } else if (!parsed.config.llmProvider) {
        errors.push('config.llmProvider is required');
      }

      if (errors.length > 0) {
        setParseError(errors.join('; '));
        setIsValid(false);
      } else {
        setParseError(null);
        setIsValid(true);
      }
    } catch (e) {
      setParseError((e as Error).message);
      setIsValid(false);
    }
  }, [jsonContent]);

  const handleValidate = async () => {
    if (!isValid) {
      toast.error('Please fix JSON syntax errors first');
      return;
    }

    setValidating(true);
    try {
      const parsed = JSON.parse(jsonContent);
      const result = await agentApi.validateAgentConfig(parsed);

      if (result.data.valid) {
        toast.success('Configuration is valid!');
      } else {
        const errorMessages = result.data.errors.map(e => e.message).join('; ');
        toast.error(`Validation failed: ${errorMessages}`);
      }
    } catch (error) {
      toast.error((error as Error).message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error('Please fix the JSON errors before creating');
      return;
    }

    setLoading(true);
    try {
      const parsed = JSON.parse(jsonContent);
      const input: CreateAgentInput = {
        name: parsed.name,
        description: parsed.description,
        agentType: parsed.agentType,
        config: parsed.config,
        isPublic: parsed.isPublic || false,
        builderType: 'json',
      };

      const response = await agentApi.createAgent(input);
      toast.success('Agent created successfully');
      navigate({ to: `/agents/${response.data.id}` });
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonContent);
    toast.success('Copied to clipboard');
  };

  const handleDownload = () => {
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent-config.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      try {
        // Validate it's valid JSON and format it
        const parsed = JSON.parse(content);
        setJsonContent(JSON.stringify(parsed, null, 2));
        toast.success('Config loaded successfully');
      } catch {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonContent);
      setJsonContent(JSON.stringify(parsed, null, 2));
      toast.success('JSON formatted');
    } catch {
      toast.error('Cannot format invalid JSON');
    }
  };

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" asChild>
            <label>
              <Upload className="h-4 w-4 mr-2" />
              Import
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleUpload}
              />
            </label>
          </Button>
          <Button variant="outline" size="sm" onClick={handleFormat}>
            Format
          </Button>
          <Button variant="outline" size="sm" onClick={handleValidate} disabled={validating || !isValid}>
            {validating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4 mr-2" />
            )}
            Validate
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {isValid ? (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Valid JSON
            </span>
          ) : (
            <span className="text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Invalid JSON
            </span>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {parseError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{parseError}</AlertDescription>
        </Alert>
      )}

      {/* Monaco Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Configuration</CardTitle>
          <CardDescription>
            Edit the JSON configuration with syntax highlighting, autocompletion, and schema validation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <Suspense fallback={<EditorSkeleton />}>
              <Editor
                height="500px"
                language="json"
                theme="vs-dark"
                value={jsonContent}
                onChange={(value) => setJsonContent(value || '')}
                onMount={handleEditorMount}
                options={{
                  minimap: { enabled: false },
                  fontSize: 13,
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  formatOnPaste: true,
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                }}
              />
            </Suspense>
          </div>
        </CardContent>
      </Card>

      {/* Schema Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration Schema</CardTitle>
          <CardDescription>
            Hover over properties in the editor for inline documentation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground space-y-2">
            <p><strong>name</strong> (required): Agent display name</p>
            <p><strong>description</strong>: Optional description</p>
            <p><strong>agentType</strong> (required): chat | research | task | data-analysis | code-assistant | support | web-browsing | multi-agent | custom</p>
            <p><strong>config.llmProvider</strong> (required): anthropic | openai | google</p>
            <p><strong>config.model</strong>: Specific model to use</p>
            <p><strong>config.systemPrompt</strong>: System instructions</p>
            <p><strong>config.temperature</strong>: 0-2, controls randomness</p>
            <p><strong>config.maxIterations</strong>: Max tool call loops</p>
            <p><strong>config.tools</strong>: Array of tool names to enable</p>
            <p><strong>isPublic</strong>: Whether agent is visible to organization</p>
          </div>
        </CardContent>
      </Card>

      {/* Create Button */}
      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={loading || !isValid} size="lg">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Agent
        </Button>
      </div>
    </div>
  );
}
